import axios from "axios";
import { filesService } from "@/services/filesService";

/**
 * Validates that a URL is a valid S3 file URL based on the backend validation logic.
 * This mirrors the validation in backend/portal/utils/file_storage.py::_regenerate_s3_link
 *
 * Valid URLs:
 * - http://localhost or http://127.0.0.1 (for local development)
 * - https://{bucket_name}.s3.amazonaws.com (any S3 bucket)
 *
 * This prevents XSS attacks via dangerous URL schemes like javascript:, data:, etc.
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is a valid S3 file URL, false otherwise
 */
export const isValidS3FileUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  // Allow localhost for local development (matches backend logic at line 88)
  const isLocalhost = url.startsWith("http://localhost") || url.startsWith("http://127.0.0.1");
  if (isLocalhost) {
    // Validate localhost format matches expected pattern: http://localhost:<port>/<bucket>/<object_key>?<query_string>
    // This matches the regex pattern used in backend at line 100
    const localhostPattern = /^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/.+?\/.+?(\?|$)/;
    return localhostPattern.test(url);
  }

  // Validate S3.amazonaws.com URLs (matches backend logic at lines 91-92)
  // Pattern: https://{bucket_name}.s3.amazonaws.com/{object_key}?{query_string}
  // The backend checks: link.startswith(f"https://{cls.bucket_name()}.s3.amazonaws.com")
  // We validate any bucket name since we don't have access to the bucket name in frontend
  const s3Pattern = /^https:\/\/[^/]+\.s3\.amazonaws\.com\/.+/;
  return s3Pattern.test(url);
};

export const uploadService = {
  async uploadFile(file, fileName) {
    // First step: Get the pre-signed POST URL
    const { upload, download } = await filesService.getPresignedUrl(fileName);
    const postURL = upload.url;
    const postFields = upload.fields;

    // Create form data for the upload
    const formData = new FormData();
    const replaceKeys = {
      aWSAccessKeyId: "AWSAccessKeyId",
      xAmzSecurityToken: "x-amz-security-token",
    };
    Object.entries(postFields).forEach(([key, value]) => {
      const newKey = replaceKeys[key] ?? key;
      formData.append(newKey, value);
    });
    formData.append("file", new File([file], fileName));

    // Second step: Upload the file to the presigned POST URL
    await axios.post(postURL, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // Third step: Verify the upload completed successfully
    const verification = await filesService.verifyUpload(download);
    if (!verification.success) {
      throw new Error(verification.error || "File upload verification failed");
    }

    return download;
  },
};
